import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import { syncFlightsFromCirium, getFlights, deleteManualFlight, updateFlight } from '../services/flightOperationsService';
import SearchableAirportSelect from '../components/SearchableAirportSelect';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const getStatusColor = (status) => {
  const statusMap = {
    'scheduled': { bg: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6' },
    'active': { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' },
    'en-route': { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' },
    'boarding': { bg: 'rgba(251, 146, 60, 0.08)', color: '#fb923c' },
    'departed': { bg: 'rgba(34, 197, 94, 0.08)', color: '#22c55e' },
    'landed': { bg: 'rgba(34, 197, 94, 0.08)', color: '#22c55e' },
    'delayed': { bg: 'rgba(239, 68, 68, 0.08)', color: '#ef4444' },
    'cancelled': { bg: 'rgba(107, 114, 128, 0.08)', color: '#6b7280' },
    'diverted': { bg: 'rgba(168, 85, 247, 0.08)', color: '#a855f7' },
    'redirected': { bg: 'rgba(236, 72, 153, 0.08)', color: '#ec4899' }
  };
  return statusMap[status?.toString().toLowerCase()] || statusMap['scheduled'];
};

const getStatusLabel = (status) => {
  if (!status) return 'Unknown';
  const key = status.toString().toLowerCase();
  const map = {
    'scheduled': 'Scheduled',
    'active': 'En-route',
    'en-route': 'En-route',
    'boarding': 'Boarding',
    'departed': 'Departed',
    'landed': 'Landed',
    'delayed': 'Delayed',
    'cancelled': 'Cancelled',
    'diverted': 'Diverted',
    'redirected': 'Redirected'
  };
  return map[key] || (status.charAt(0).toUpperCase() + status.slice(1));
};

const toDatetimeLocal = (isoStr) => {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const toISOStringOrNull = (val) => {
  if (!val) return null;
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  const baseUrl = process.env.REACT_APP_IMG_BASE_URL || 'http://localhost:3001/';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanPath = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
  return `${cleanBase}${cleanPath}`;
};

const AirlineLogoBadge = ({ logo, iata }) => {
  const [err, setErr] = useState(false);
  const logoUrl = getLogoUrl(logo);

  if (logoUrl && !err) {
    return (
      <img
        src={logoUrl}
        alt={iata || 'Airline'}
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          objectFit: 'contain',
          background: 'white',
          border: '1px solid #E2E8F0',
          padding: 2
        }}
        onError={() => setErr(true)}
      />
    );
  }

  return (
    <div style={{
      width: 26,
      height: 26,
      borderRadius: 7,
      background: '#0078D2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 9,
      fontWeight: 800,
      color: 'white'
    }}>
      {iata || 'N/A'}
    </div>
  );
};

const formatGateTerminal = (gate, terminal) => {
  const g = gate ? `${gate} (Gate)` : '';
  const t = terminal ? `${terminal} (Terminal)` : '';

  if (g && t) return `${g} / ${t}`;
  if (t) return t;
  if (g) return g;
  return '—';
};

const FlightOperations = () => {
  const navigate = useNavigate();
  const { refreshKey } = useRefresh();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editing, setEditing] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Sync Form States
  const [syncFormData, setSyncFormData] = useState({
    airport: '',
    type: 'departure',
    date: new Date().toISOString().split('T')[0]
  });

  // Edit Form States
  const [editFormData, setEditFormData] = useState({
    status: 'scheduled'
  });

  const fetchFlights = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...(searchQuery && { search: searchQuery }),
        ...(filterStatus && { status: filterStatus })
      };
      const res = await getFlights(params);
      setFlights(res?.data || []);
      setTotalPages(res?.meta?.totalPages || 1);
      setTotalRecords(res?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load flights");
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, filterStatus]);

  const prevSearch = useRef(searchQuery);

  useEffect(() => {
    const searchChanged = prevSearch.current !== searchQuery;
    prevSearch.current = searchQuery;

    const timer = setTimeout(() => {
      fetchFlights();
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [fetchFlights, refreshKey]);

  const handleSyncSubmit = async (e) => {
    e.preventDefault();
    if (!syncFormData.airport) {
      return toast.warning("Please select an airport");
    }

    setSyncing(true);
    try {
      const payload = {
        iata: syncFormData.airport,
        type: syncFormData.type,
        date: syncFormData.date
      };
      const response = await syncFlightsFromCirium(payload);
      
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success("Flights synced successfully from Cirium");
        setShowSyncModal(false);
        fetchFlights();
      } else {
        toast.error(response?.data?.message || "Sync failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong during sync");
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteFlight = (flight) => {
    setSelectedFlight(flight);
    setShowDeleteModal(true);
  };

  const confirmDeleteFlight = async () => {
    if (!selectedFlight) return;
    
    setDeleting(true);
    try {
      const response = await deleteManualFlight(selectedFlight.id);
      if (response && (response.status === 200 || response.status === 204 || !response.error)) {
        toast.success("Flight deleted successfully");
        setShowDeleteModal(false);
        setSelectedFlight(null);
        fetchFlights();
      } else {
        toast.error("Failed to delete flight");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error deleting flight");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditFlight = (flight) => {
    setSelectedFlight(flight);
    const departure = flight.departure || flight.StatusDetails?.find(d => d.type === 'departure') || {};
    const arrival = flight.arrival || flight.StatusDetails?.find(d => d.type === 'arrival') || {};
    
    setEditFormData({
      status: flight.status || 'scheduled',
      boarding_time: toDatetimeLocal(flight.boarding_time),
      gate_closes: toDatetimeLocal(flight.gate_closes),
      duration: flight.duration !== undefined && flight.duration !== null ? flight.duration : '',
      buffer_time: flight.buffer_time !== undefined && flight.buffer_time !== null ? flight.buffer_time : '',
      departure: {
        terminal: departure.terminal || flight.terminal || '',
        gate: departure.gate || flight.gate || '',
        scheduled_time: toDatetimeLocal(departure.scheduled_time || flight.scheduled_time),
        estimated_time: toDatetimeLocal(departure.estimated_time || flight.estimated_time),
        actual_time: toDatetimeLocal(departure.actual_time || flight.actual_time)
      },
      arrival: {
        terminal: arrival.terminal || '',
        gate: arrival.gate || '',
        baggage: arrival.baggage || '',
        scheduled_time: toDatetimeLocal(arrival.scheduled_time),
        estimated_time: toDatetimeLocal(arrival.estimated_time),
        actual_time: toDatetimeLocal(arrival.actual_time)
      }
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdateFlight = async (e) => {
    e.preventDefault();
    if (!selectedFlight) return;

    setEditing(true);
    try {
      const payload = {
        status: editFormData.status
      };

      const response = await updateFlight(selectedFlight.id, payload);
      if (response && (response.status === 200 || response.status === 204 || !response.error)) {
        toast.success("Flight status updated successfully");
        setIsEditing(false);
        setSelectedFlight(null);
        fetchFlights();
      } else {
        toast.error("Failed to update flight");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating flight");
    } finally {
      setEditing(false);
    }
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading && flights.length === 0) return <Spinner />;

  return (
    <div>
      <Topbar title="Flight Operations" subtitle="Manage real-time flight instances and sync with Cirium" />

      {/* SYNC MODAL */}
      {showSyncModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 450, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
            {syncing && (
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                zIndex: 20,
                backdropFilter: 'blur(3px)'
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  border: '3.5px solid #E2E8F0',
                  borderTopColor: 'var(--blue)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
                    Syncing Flights from Cirium...
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--grey)' }}>
                    Fetching latest flight schedules. Please wait.
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, background: '#EEF6FF', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>Sync from Cirium</h3>
                <p style={{ fontSize: 12, color: 'var(--grey)' }}>Fetch latest flight data from Cirium</p>
              </div>
            </div>

            <form onSubmit={handleSyncSubmit}>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Airport <span className="req">*</span></label>
                <SearchableAirportSelect
                  value={syncFormData.airport}
                  onChange={(code) => setSyncFormData({ ...syncFormData, airport: code })}
                  placeholder="Select Airport"
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Date <span className="req">*</span></label>
                <input
                  type="date"
                  className="form-input"
                  value={syncFormData.date}
                  onChange={e => setSyncFormData({ ...syncFormData, date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">Direction <span className="req">*</span></label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: `1.5px solid ${syncFormData.type === 'departure' ? 'var(--blue)' : '#E8EDF3'}`, borderRadius: 12, cursor: 'pointer', background: syncFormData.type === 'departure' ? 'rgba(102,178,255,0.05)' : 'white' }}>
                    <input type="radio" name="syncType" checked={syncFormData.type === 'departure'} onChange={() => setSyncFormData({ ...syncFormData, type: 'departure' })} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Departure</span>
                  </label>
                  <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: `1.5px solid ${syncFormData.type === 'arrival' ? 'var(--blue)' : '#E8EDF3'}`, borderRadius: 12, cursor: 'pointer', background: syncFormData.type === 'arrival' ? 'rgba(102,178,255,0.05)' : 'white' }}>
                    <input type="radio" name="syncType" checked={syncFormData.type === 'arrival'} onChange={() => setSyncFormData({ ...syncFormData, type: 'arrival' })} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Arrival</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowSyncModal(false)} disabled={syncing}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 8 }} disabled={syncing}>
                  {syncing ? (
                    <>
                      <span className="spin-anim" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%' }} />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    'Start Sync'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && selectedFlight && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10, textAlign: 'center' }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5, textAlign: 'center' }}>
              Are you sure you want to delete flight <strong>{selectedFlight.flight_number || selectedFlight.id}</strong>? This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { setShowDeleteModal(false); setSelectedFlight(null); }} disabled={deleting}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', background: '#F87171', borderColor: '#F87171' }} onClick={confirmDeleteFlight} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.3 — FLIGHT MANAGEMENT & CIRIUM SYNC</div>

        <div className="card">
          {/* IN-PAGE EDIT FORM SECTION */}
          {isEditing && selectedFlight && (
            <div style={{ borderBottom: '2px solid #F0F4F8', padding: '24px 26px', background: '#F8FAFC', borderRadius: '20px 20px 0 0' }}>
              <div style={{ marginBottom: 20 }}>
                <div className="screen-label" style={{ marginBottom: 4, fontSize: 13, color: 'var(--blue)', fontWeight: 700 }}>
                  Modify Flight #{selectedFlight.flight_number || selectedFlight.id}
                </div>
                <div style={{ fontSize: 12, color: 'var(--grey)' }}>
                  {selectedFlight.airline?.name ? `${selectedFlight.airline.name} • ` : ''}
                  {selectedFlight.origin_iata || selectedFlight.origin || 'N/A'} → {selectedFlight.destination_iata || selectedFlight.destination || 'N/A'}
                </div>
              </div>

              <form onSubmit={handleUpdateFlight}>
                {/* General Parameters */}
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 14, borderBottom: '1px solid #E8EDF3', paddingBottom: 6 }}>
                    General Parameters
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Status <span className="req">*</span></label>
                      <select
                        className="form-select"
                        value={editFormData.status}
                        onChange={e => setEditFormData({ ...editFormData, status: e.target.value })}
                        required
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="active">En-route</option>
                        <option value="boarding">Boarding</option>
                        <option value="landed">Landed</option>
                        <option value="delayed">Delayed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="diverted">Diverted</option>
                        <option value="redirected">Redirected</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Boarding Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.boarding_time}
                        onChange={e => setEditFormData({ ...editFormData, boarding_time: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Gate Closes</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.gate_closes}
                        onChange={e => setEditFormData({ ...editFormData, gate_closes: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Duration (minutes)</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 150"
                        value={editFormData.duration}
                        onChange={e => setEditFormData({ ...editFormData, duration: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Buffer Time (minutes)</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g. 15"
                        value={editFormData.buffer_time}
                        onChange={e => setEditFormData({ ...editFormData, buffer_time: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Departure Parameters */}
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 14, borderBottom: '1px solid #E8EDF3', paddingBottom: 6 }}>
                    Departure Parameters
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Departure Terminal</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder='e.g. "D" or "1"'
                        value={editFormData.departure.terminal}
                        onChange={e => setEditFormData({ ...editFormData, departure: { ...editFormData.departure, terminal: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Departure Gate</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder='e.g. "D24" or "B22"'
                        value={editFormData.departure.gate}
                        onChange={e => setEditFormData({ ...editFormData, departure: { ...editFormData.departure, gate: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Scheduled Departure Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.departure.scheduled_time}
                        onChange={e => setEditFormData({ ...editFormData, departure: { ...editFormData.departure, scheduled_time: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Estimated Departure Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.departure.estimated_time}
                        onChange={e => setEditFormData({ ...editFormData, departure: { ...editFormData.departure, estimated_time: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Actual Departure Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.departure.actual_time}
                        onChange={e => setEditFormData({ ...editFormData, departure: { ...editFormData.departure, actual_time: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>

                {/* Arrival Parameters */}
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 14, borderBottom: '1px solid #E8EDF3', paddingBottom: 6 }}>
                    Arrival Parameters
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Arrival Terminal</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder='e.g. "N" or "2"'
                        value={editFormData.arrival.terminal}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, terminal: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Arrival Gate</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder='e.g. "C14"'
                        value={editFormData.arrival.gate}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, gate: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Baggage Claim</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder='e.g. "B5" or "Claim 4"'
                        value={editFormData.arrival.baggage}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, baggage: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Scheduled Arrival Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.arrival.scheduled_time}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, scheduled_time: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Estimated Arrival Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.arrival.estimated_time}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, estimated_time: e.target.value } })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Actual Arrival Time</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={editFormData.arrival.actual_time}
                        onChange={e => setEditFormData({ ...editFormData, arrival: { ...editFormData.arrival, actual_time: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 20 }}>
                  <button type="button" className="btn btn-outline" onClick={() => { setIsEditing(false); setSelectedFlight(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={editing}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                    {editing ? 'Updating...' : 'Update Flight'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* TOOLBAR */}
          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              <input
                className="search-input"
                placeholder="Search flights..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="active">En-route</option>
              <option value="boarding">Boarding</option>
              <option value="landed">Landed</option>
              <option value="delayed">Delayed</option>
              <option value="cancelled">Cancelled</option>
              <option value="diverted">Diverted</option>
              <option value="redirected">Redirected</option>
            </select>
            <button
              className="btn btn-sync"
              onClick={() => setShowSyncModal(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Sync Flights from Cirium
            </button>
          </div>

          {/* FLIGHTS TABLE */}
          <table className="tbl">
            <thead>
              <tr>
                <th>Flight #</th>
                <th>Airline</th>
                <th>Route</th>
                <th>Scheduled Time</th>
                <th>Gate / Terminal</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}><Spinner /></td></tr>
              ) : flights.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No flights found. Sync flights from Cirium to get started.</td></tr>
              ) : (
                flights.map((f) => {
                  const statusColor = getStatusColor(f.status);
                  const dep = f.departure || f.StatusDetails?.find(d => d.type === 'departure') || {};
                  const arr = f.arrival || f.StatusDetails?.find(d => d.type === 'arrival') || {};
                  const depGate = dep.gate || f.gate;
                  const depTerm = dep.terminal || f.terminal;
                  const arrGate = arr.gate || f.arrival_gate || f.arrival?.gate;
                  const arrTerm = arr.terminal || f.arrival_terminal || f.arrival?.terminal;

                  const depText = formatGateTerminal(depGate, depTerm);
                  const arrText = formatGateTerminal(arrGate, arrTerm);

                  return (
                    <tr key={f.id}>
                      <td style={{ fontWeight: 700, fontSize: 14, fontFamily: "'Space Grotesk', sans-serif", cursor: 'pointer', color: 'var(--blue)' }} onClick={() => navigate(`/flights/${f.id}`)} title="View flight details">
                        {f.flight_number || f.id || '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AirlineLogoBadge logo={f.airline?.logo} iata={f.airline?.iata} />
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{f.airline?.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                          {f.origin_iata && f.destination_iata ? `${f.origin_iata} → ${f.destination_iata}` : f.origin && f.destination ? `${f.origin} → ${f.destination}` : '—'}
                        </div>
                        {f.origin_name && f.destination_name && (
                          <div style={{ fontSize: 11, color: 'var(--grey)' }}>
                            {f.origin_name} to {f.destination_name}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--dark)' }}>
                        <div>{f.scheduled_time ? new Date(f.scheduled_time).toLocaleString() : 'N/A'}</div>
                        {f.estimated_time && (
                          <div style={{ fontSize: 11, color: 'var(--grey)' }}>Est: {new Date(f.estimated_time).toLocaleTimeString()}</div>
                        )}
                      </td>
                      <td>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>
                            <span style={{ fontSize: 10, color: 'var(--blue)', fontWeight: 700, marginRight: 4 }}>DEP:</span>
                            {depText}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginTop: 3 }}>
                            <span style={{ fontSize: 10, color: '#10b981', fontWeight: 700, marginRight: 4 }}>ARR:</span>
                            {arrText}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          background: statusColor.bg,
                          color: statusColor.color,
                          display: 'inline-block'
                        }}>
                          {getStatusLabel(f.status)}
                        </span>
                      </td>
                      <td>
                        <div className="action-btns">
                          <button
                            className="action-btn"
                            onClick={() => navigate(`/flights/${f.id}`)}
                            title="View details"
                          >
                            <Svg d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => handleEditFlight(f)}
                            title="Edit flight"
                          >
                            <Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={() => handleDeleteFlight(f)}
                            disabled={deleting}
                            title="Delete flight"
                          >
                            <Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {flights.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> flights
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightOperations;
